// ===================================
// TRENCH QUEST - Game Master
// Main orchestrator for dynamic AI-driven gameplay
// ===================================

import type {
  GameSession,
  GeneratedSituation,
  GeneratedOutcome,
  GeneratedImage,
  PlayerAction,
  ConsequenceLog,
  SituationGenerationRequest,
  DynamicAction,
} from '@/types'

import { generateTextDirect, parseAIJsonResponse } from '@/services/geminiClient'
import { imageCache, createCacheKey } from '@/services/imageCache'
import { getRealMarketContext } from '@/services/marketData'
import {
  generateSituationViaCloud,
  resolveChoiceViaCloud,
  generateImageViaCloud,
} from '@/services/gameMasterApi'
import {
  buildGameMasterContext,
  checkSituationTriggers,
  createChoiceAction,
  createConsequenceLog,
  findRelevantConsequences,
} from './contextBuilder'
import {
  buildSituationGenerationPrompt,
  generateSituationId,
  TRIGGER_PROMPTS,
  GAME_MASTER_SYSTEM_PROMPT,
} from './prompts/gameMasterPrompt'
import { buildOutcomePrompt, getOutcomeQuote, OUTCOME_SYSTEM_PROMPT } from './prompts/outcomePrompt'
import { getStyleForCategory } from './prompts/imagePrompt'
import {
  buildDynamicActionsPrompt,
  getDefaultDynamicActions,
  parseDynamicActionsResponse,
  DYNAMIC_ACTIONS_SYSTEM_PROMPT,
} from './prompts/dynamicActionsPrompt'

/**
 * Game Master singleton - orchestrates all AI generation
 */
export class GameMaster {
  private static instance: GameMaster

  // State tracking
  private currentSituation: GeneratedSituation | null = null
  private consequenceLog: ConsequenceLog[] = []
  private recentActions: PlayerAction[] = []
  private lastSituationTime: Date | null = null
  private generatedSituationIds: Set<string> = new Set()

  // Loading state
  private isGenerating = false

  // Cloud function preference
  private useCloudFunctions = true

  private constructor() {}

  static getInstance(): GameMaster {
    if (!GameMaster.instance) {
      GameMaster.instance = new GameMaster()
    }
    return GameMaster.instance
  }

  /**
   * Get current state
   */
  getCurrentSituation(): GeneratedSituation | null {
    return this.currentSituation
  }

  getConsequenceLog(): ConsequenceLog[] {
    return this.consequenceLog
  }

  getRecentActions(): PlayerAction[] {
    return this.recentActions
  }

  isCurrentlyGenerating(): boolean {
    return this.isGenerating
  }

  /**
   * Enable or disable cloud functions
   */
  setUseCloudFunctions(enabled: boolean): void {
    this.useCloudFunctions = enabled
  }

  /**
   * Check if cloud functions are enabled
   */
  isUsingCloudFunctions(): boolean {
    return this.useCloudFunctions
  }

  /**
   * Add an action to recent history
   */
  addAction(action: PlayerAction): void {
    this.recentActions.unshift(action)
    // Keep last 20 actions
    if (this.recentActions.length > 20) {
      this.recentActions = this.recentActions.slice(0, 20)
    }
  }

  /**
   * Generate a new situation
   * Tries cloud functions first, falls back to local generation
   */
  async generateSituation(
    session: GameSession,
    request: Partial<SituationGenerationRequest> = {}
  ): Promise<GeneratedSituation> {
    if (this.isGenerating) {
      throw new Error('Already generating a situation')
    }

    this.isGenerating = true

    try {
      // Build trigger context string
      let triggerContext = ''
      if (request.trigger === 'market_event') {
        triggerContext = TRIGGER_PROMPTS.market_pump('SOL', 10)
      } else if (request.trigger === 'time_skip') {
        triggerContext = TRIGGER_PROMPTS.time_skip(2)
      } else if (request.trigger === 'travel') {
        triggerContext = TRIGGER_PROMPTS.new_location(session.world.currentLocation)
      }

      // Check if near goal
      const progressPercent = (session.player.portfolio.totalValue / session.goal.targetAmount) * 100
      if (progressPercent >= 90) {
        triggerContext = TRIGGER_PROMPTS.goal_near(progressPercent)
      }

      // Get real market data
      const marketData = await getRealMarketContext()

      // Try cloud functions first
      if (this.useCloudFunctions) {
        try {
          const situation = await generateSituationViaCloud(
            session,
            this.recentActions,
            this.consequenceLog,
            Array.from(this.generatedSituationIds),
            marketData,
            triggerContext
          )

          // Ensure unique ID
          situation.id = generateSituationId()

          // Track generated situation
          this.generatedSituationIds.add(situation.id)
          this.currentSituation = situation
          this.lastSituationTime = new Date()

          return situation
        } catch (cloudError) {
          // Don't silently fall back - throw the cloud error so user can see it
          // Local generation requires VITE_GEMINI_API_KEY which isn't set
          console.error('Cloud function failed:', cloudError)
          const errorMessage = cloudError instanceof Error ? cloudError.message : String(cloudError)
          throw new Error(`Cloud AI failed: ${errorMessage}`)
        }
      }

      // Fallback to local generation
      const context = await buildGameMasterContext(
        session,
        this.recentActions,
        this.consequenceLog,
        Array.from(this.generatedSituationIds)
      )

      const userPrompt = buildSituationGenerationPrompt(context) +
        (triggerContext ? `\n\n## TRIGGER CONTEXT\n${triggerContext}` : '')

      const response = await generateTextDirect(GAME_MASTER_SYSTEM_PROMPT, userPrompt)
      const situation = parseAIJsonResponse<GeneratedSituation>(response)

      // Ensure unique ID
      situation.id = generateSituationId()

      // Track generated situation
      this.generatedSituationIds.add(situation.id)
      this.currentSituation = situation
      this.lastSituationTime = new Date()

      return situation
    } finally {
      this.isGenerating = false
    }
  }

  /**
   * Resolve a player's choice
   * Tries cloud functions first, falls back to local generation
   */
  async resolveChoice(
    session: GameSession,
    choiceId: string
  ): Promise<{
    outcome: GeneratedOutcome
    quote: string
    action: PlayerAction
    consequence?: ConsequenceLog
  }> {
    if (!this.currentSituation) {
      throw new Error('No current situation to resolve')
    }

    const choice = this.currentSituation.choices?.find(c => c.id === choiceId)
    if (!choice) {
      // No pre-defined choice found, create a synthetic one from the choiceId
      // This supports both old choice-based flow and new free-form flow
      throw new Error(`Invalid choice: ${choiceId}`)
    }

    this.isGenerating = true

    try {
      let outcome: GeneratedOutcome

      // Get real market data
      const marketData = await getRealMarketContext()

      // Try cloud functions first
      if (this.useCloudFunctions) {
        try {
          outcome = await resolveChoiceViaCloud(
            this.currentSituation,
            choiceId,
            session,
            marketData
          )
        } catch (cloudError) {
          console.warn('Cloud function failed, falling back to local generation:', cloudError)

          // Fallback to local generation
          const context = await buildGameMasterContext(
            session,
            this.recentActions,
            this.consequenceLog,
            Array.from(this.generatedSituationIds)
          )

          const userPrompt = buildOutcomePrompt(this.currentSituation, choice, context)
          const response = await generateTextDirect(OUTCOME_SYSTEM_PROMPT, userPrompt)
          outcome = parseAIJsonResponse<GeneratedOutcome>(response)
        }
      } else {
        // Local generation only
        const context = await buildGameMasterContext(
          session,
          this.recentActions,
          this.consequenceLog,
          Array.from(this.generatedSituationIds)
        )

        const userPrompt = buildOutcomePrompt(this.currentSituation, choice, context)
        const response = await generateTextDirect(OUTCOME_SYSTEM_PROMPT, userPrompt)
        outcome = parseAIJsonResponse<GeneratedOutcome>(response)
      }

      // Get a fitting quote
      const quote = getOutcomeQuote(outcome)

      // Create action record
      const action = createChoiceAction(
        this.currentSituation,
        choiceId,
        outcome.narrative.slice(0, 100)
      )
      this.addAction(action)

      // Create consequence log if there are flags
      let consequence: ConsequenceLog | undefined
      if (outcome.consequenceFlags && outcome.consequenceFlags.length > 0) {
        consequence = createConsequenceLog(
          this.currentSituation.id,
          choiceId,
          outcome.consequenceFlags,
          outcome.narrative.slice(0, 200),
          this.currentSituation.characters.map(c => c.id)
        )
        this.consequenceLog.push(consequence)
      }

      // Clear current situation (choice resolved)
      const resolvedSituation = this.currentSituation
      this.currentSituation = null

      // If outcome triggers follow-up, we'll generate one next tick
      if (outcome.triggersFollowUp) {
        // Schedule follow-up generation
        setTimeout(() => {
          this.generateSituation(session, { trigger: 'follow_up', previousSituationId: resolvedSituation.id })
        }, 100)
      }

      return { outcome, quote, action, consequence }
    } finally {
      this.isGenerating = false
    }
  }

  /**
   * Resolve a custom player action (user-typed action)
   * Creates a synthetic choice and resolves it
   */
  async resolveCustomChoice(
    session: GameSession,
    customAction: string,
    situation: GeneratedSituation
  ): Promise<{
    outcome: GeneratedOutcome
    quote: string
    action: PlayerAction
    consequence?: ConsequenceLog
  }> {
    this.isGenerating = true

    // Store reference to situation if not already set
    if (!this.currentSituation) {
      this.currentSituation = situation
    }

    try {
      // Create a synthetic choice for the custom action
      const customChoice = {
        id: `custom-${Date.now()}`,
        text: customAction,
        riskLevel: 'moderate' as const,
        hint: 'User-defined action',
      }

      // Get real market data
      const marketData = await getRealMarketContext()

      let outcome: GeneratedOutcome

      // For custom actions, we create a modified situation with the custom choice
      const situationWithCustom = {
        ...situation,
        choices: [...(situation.choices || []), customChoice],
      }

      // Try cloud functions first
      if (this.useCloudFunctions) {
        try {
          outcome = await resolveChoiceViaCloud(
            situationWithCustom,
            customChoice.id,
            session,
            marketData
          )
        } catch (cloudError) {
          console.warn('Cloud function failed for custom choice, falling back to local generation:', cloudError)

          // Fallback to local generation
          const context = await buildGameMasterContext(
            session,
            this.recentActions,
            this.consequenceLog,
            Array.from(this.generatedSituationIds)
          )

          const userPrompt = buildOutcomePrompt(situationWithCustom, customChoice, context)
          const response = await generateTextDirect(OUTCOME_SYSTEM_PROMPT, userPrompt)
          outcome = parseAIJsonResponse<GeneratedOutcome>(response)
        }
      } else {
        // Local generation only
        const context = await buildGameMasterContext(
          session,
          this.recentActions,
          this.consequenceLog,
          Array.from(this.generatedSituationIds)
        )

        const userPrompt = buildOutcomePrompt(situationWithCustom, customChoice, context)
        const response = await generateTextDirect(OUTCOME_SYSTEM_PROMPT, userPrompt)
        outcome = parseAIJsonResponse<GeneratedOutcome>(response)
      }

      // Get a fitting quote
      const quote = getOutcomeQuote(outcome)

      // Create action record for custom action
      const action: PlayerAction = {
        id: customChoice.id,
        type: 'choice',
        description: `Custom action: ${customAction.slice(0, 100)}`,
        timestamp: new Date(),
        situationId: situation.id,
        choiceId: customChoice.id,
      }
      this.addAction(action)

      // Create consequence log if there are flags
      let consequence: ConsequenceLog | undefined
      if (outcome.consequenceFlags && outcome.consequenceFlags.length > 0) {
        consequence = createConsequenceLog(
          situation.id,
          customChoice.id,
          outcome.consequenceFlags,
          outcome.narrative.slice(0, 200),
          situation.characters.map(c => c.id)
        )
        this.consequenceLog.push(consequence)
      }

      // Clear current situation (choice resolved)
      this.currentSituation = null

      // If outcome triggers follow-up, generate one
      if (outcome.triggersFollowUp) {
        setTimeout(() => {
          this.generateSituation(session, { trigger: 'follow_up', previousSituationId: situation.id })
        }, 100)
      }

      return { outcome, quote, action, consequence }
    } finally {
      this.isGenerating = false
    }
  }

  /**
   * Generate a situation from a free-form player action (adventure game style)
   * This is called when the player types an action without an active situation
   * e.g., "look around", "go to the coffee shop", "check my phone"
   */
  async generateSituationFromAction(
    session: GameSession,
    playerAction: string
  ): Promise<GeneratedSituation> {
    if (this.isGenerating) {
      throw new Error('Already generating a situation')
    }

    this.isGenerating = true
    const actionLower = playerAction.toLowerCase()

    try {
      // ===========================================
      // DETECT UI-RELATED INTENTS (don't need AI)
      // ===========================================

      // Trading intents → Direct to TERMINAL
      const tradingPatterns = [
        /\b(buy|sell|trade|swap|purchase|get|acquire)\b.*\b(sol|bonk|wif|jup|token|coin|crypto)/i,
        /\b(buy|sell|trade|swap)\b\s+(some|a|the)?\s*\w+/i,
        /\bhow\s+(do\s+i\s+)?(buy|sell|trade)/i,
        /\bwhere\s+(can\s+i\s+)?(buy|sell|trade)/i,
        /\bi\s+want\s+to\s+(buy|sell|trade)/i,
      ]

      const isTradingIntent = tradingPatterns.some(p => p.test(actionLower))
      if (isTradingIntent) {
        return this.createUIRedirectSituation(
          'terminal',
          'Trading Terminal',
          `You reach for your trading terminal...\n\nTo buy or sell tokens, use the **TERMINAL** tab. That's where you can:\n- View current token prices\n- Execute buy orders with your USDC\n- Sell tokens from your portfolio\n- See your trading history\n\n**Click the TERMINAL tab** to start trading.`
        )
      }

      // Portfolio/balance checking → Direct to PORTFOLIO
      const portfolioPatterns = [
        /\b(check|view|see|show|what('s| is))\s+(my\s+)?(portfolio|balance|holdings|wallet|money|cash|funds)/i,
        /\bhow\s+much\s+(money|cash|do i have)/i,
        /\bwhat\s+do\s+i\s+(have|own|hold)/i,
        /\bmy\s+(portfolio|balance|holdings)/i,
      ]

      const isPortfolioIntent = portfolioPatterns.some(p => p.test(actionLower))
      if (isPortfolioIntent) {
        return this.createUIRedirectSituation(
          'portfolio',
          'Checking Portfolio',
          `You pull out your phone to check your portfolio...\n\nYour holdings and balance are in the **PORTFOLIO** tab. There you can see:\n- All tokens you currently hold\n- Your cash balance (USDC)\n- Profit/loss on each position\n- Total portfolio value\n\n**Click the PORTFOLIO tab** to view your holdings.`
        )
      }

      // Price/market checking → Direct to TERMINAL
      const pricePatterns = [
        /\b(check|view|see|what('s| is))\s+(the\s+)?(price|market|prices)/i,
        /\bhow\s+much\s+is\s+\w+\s+(worth|trading|at)/i,
        /\b(sol|bonk|wif|jup)\s+price/i,
        /\bprice\s+of\s+\w+/i,
        /\bmarket\s+(conditions?|data|info)/i,
      ]

      const isPriceIntent = pricePatterns.some(p => p.test(actionLower))
      if (isPriceIntent) {
        return this.createUIRedirectSituation(
          'terminal',
          'Checking Prices',
          `You open up the market data...\n\nToken prices and market info are in the **TERMINAL** tab. There you can see:\n- Current prices for all tokens\n- 24h price changes\n- Market trends\n- Execute trades\n\n**Click the TERMINAL tab** to view prices.`
        )
      }

      // Stats/traits checking → Direct to STATUS
      const statsPatterns = [
        /\b(check|view|see|what('s| is|'re| are))\s+(my\s+)?(stats?|traits?|reputation|score|status)/i,
        /\bhow\s+(am\s+i\s+doing|is\s+my\s+reputation)/i,
        /\bmy\s+(degen|trust|alpha|wealth)\s*score/i,
        /\bwhat\s+(day|time)\s+is\s+it/i,
        /\bhow\s+much\s+time\s+(left|remaining|do i have)/i,
      ]

      const isStatsIntent = statsPatterns.some(p => p.test(actionLower))
      if (isStatsIntent) {
        return this.createUIRedirectSituation(
          'status',
          'Checking Status',
          `You take a moment to assess your situation...\n\nYour stats and game progress are in the **STATUS** tab. There you can see:\n- Current game day and time remaining\n- Your goal progress\n- Degen Score, Trust Score, and other traits\n- Your reputation level\n\n**Click the STATUS tab** to view your stats.`
        )
      }

      // Help/meta questions → Give hints
      const helpPatterns = [
        /\b(help|hint|what\s+can\s+i\s+do|what\s+are\s+my\s+options|how\s+do\s+i\s+play)/i,
        /\bwhat\s+should\s+i\s+do/i,
        /\bi('m| am)\s+(stuck|confused|lost)/i,
        /\bhow\s+does\s+this\s+(work|game\s+work)/i,
      ]

      const isHelpIntent = helpPatterns.some(p => p.test(actionLower))
      if (isHelpIntent) {
        return this.createUIRedirectSituation(
          'help',
          'Game Guide',
          `Welcome to the trenches, anon. Here's how to play:\n\n**EXPLORE:** Type actions like "look around", "talk to [person]", "examine [thing]", or "go to [place]".\n\n**TRADE:** Use the **TERMINAL** tab to buy/sell tokens. The story gives you alpha - you decide what to do with it.\n\n**GOAL:** Grow your portfolio to hit your target before time runs out.\n\n**TABS:**\n- **STATUS** - Your stats, day, and goal progress\n- **TERMINAL** - Buy/sell tokens, view prices\n- **PORTFOLIO** - Your holdings and P&L\n\nType any action to continue your adventure. WAGMI.`
        )
      }

      // ===========================================
      // TRAVEL INTENT DETECTION
      // ===========================================

      // Detect if player is trying to go somewhere
      const travelPatterns = [
        /go\s+(to\s+)?(the\s+)?(.+)/i,
        /head\s+(to\s+)?(the\s+)?(.+)/i,
        /leave\s+(and\s+)?(go\s+)?(to\s+)?(the\s+)?(.+)?/i,
        /travel\s+(to\s+)?(the\s+)?(.+)/i,
        /visit\s+(the\s+)?(.+)/i,
        /return\s+(to\s+)?(the\s+)?(.+)/i,
        /go\s+home/i,
        /head\s+home/i,
      ]

      let intendedLocation = ''
      for (const pattern of travelPatterns) {
        const match = playerAction.match(pattern)
        if (match) {
          // Get the last non-empty capture group (the destination)
          const destination = match.filter(m => m && m.trim()).pop()
          if (destination && !['go', 'to', 'the', 'and', 'head', 'leave', 'travel', 'visit', 'return'].includes(destination.toLowerCase())) {
            intendedLocation = destination
          }
          break
        }
      }

      // Special case for "go home" type actions
      if (playerAction.toLowerCase().includes('home')) {
        intendedLocation = 'apartment'
      }

      // Create a trigger context based on the player's action
      const actionContext = `
## PLAYER ACTION (CRITICAL - READ CAREFULLY!)
The player has typed: "${playerAction}"

## RESPONDING TO PLAYER INTENT (MOST IMPORTANT RULE!)

You MUST directly acknowledge and respond to EXACTLY what the player said they want to do.

**Examples of GOOD responses:**
- Player: "sit on the bed" → Scene opens with them sitting on the bed, describe what they see from there
- Player: "examine the laptop" → Describe them looking at the laptop, what's on the screen
- Player: "talk to the girl" → They approach and start a conversation with her
- Player: "check my phone" → They pull out their phone, describe notifications/messages
- Player: "order a drink" → They order, describe the bartender, the drink arriving
- Player: "look out the window" → Describe the view, what they notice outside

**Examples of BAD responses (NEVER DO THIS):**
- Player: "sit on the bed" → Scene ignores this and describes them at a nightclub
- Player: "examine the laptop" → Scene talks about something completely unrelated
- Player: "talk to the girl" → Scene has them alone with no mention of the girl

**THE RULE:** The FIRST thing in your description should acknowledge the player's action.
Start the scene with the RESULT of what they did. Then expand from there.

${intendedLocation ? `
## LOCATION CHANGE REQUESTED
The player wants to go to: "${intendedLocation}"
Set the scene at "${intendedLocation}" (or closest matching location).
"home" = apartment, "club" = nightclub-vip, "coffee" = coffee-shop
` : ''}

## SCENE GENERATION GUIDELINES

1. **START with the action result** - First sentence should show their action happening
2. **Describe from their new perspective** - What do they see/hear/notice from this position?
3. **Flow naturally** - The scene should feel like a direct continuation
4. **Add interesting details** - Things to examine, people to interact with, opportunities
5. **Use bold hints** - **Bold text** for interactive elements they might explore next

## ADVENTURE GAME STYLE

This is a text adventure - focus on DESCRIPTION over dialog:
- Rich environmental details (sights, sounds, atmosphere)
- Interesting things to notice and investigate
- NPCs described through actions and appearance
- Subtle hooks for further exploration
- 2-4 paragraphs of immersive prose
`

      // Get real market data
      const marketData = await getRealMarketContext()

      // Try cloud functions first
      if (this.useCloudFunctions) {
        try {
          const situation = await generateSituationViaCloud(
            session,
            this.recentActions,
            this.consequenceLog,
            Array.from(this.generatedSituationIds),
            marketData,
            actionContext
          )

          // Ensure unique ID
          situation.id = generateSituationId()

          // For adventure style, clear any pre-generated choices (we don't use them)
          // Keep the description and context only
          situation.choices = []

          // Track generated situation
          this.generatedSituationIds.add(situation.id)
          this.currentSituation = situation
          this.lastSituationTime = new Date()

          return situation
        } catch (cloudError) {
          console.error('Cloud function failed:', cloudError)
          const errorMessage = cloudError instanceof Error ? cloudError.message : String(cloudError)
          throw new Error(`Cloud AI failed: ${errorMessage}`)
        }
      }

      // Fallback to local generation
      const context = await buildGameMasterContext(
        session,
        this.recentActions,
        this.consequenceLog,
        Array.from(this.generatedSituationIds)
      )

      const userPrompt = buildSituationGenerationPrompt(context) + `\n\n${actionContext}`

      const response = await generateTextDirect(GAME_MASTER_SYSTEM_PROMPT, userPrompt)
      const situation = parseAIJsonResponse<GeneratedSituation>(response)

      // Ensure unique ID and clear choices
      situation.id = generateSituationId()
      situation.choices = []

      // Track generated situation
      this.generatedSituationIds.add(situation.id)
      this.currentSituation = situation
      this.lastSituationTime = new Date()

      return situation
    } finally {
      this.isGenerating = false
    }
  }

  /**
   * Create a pre-built situation for UI redirects (no AI needed)
   * Used when player asks about trading, portfolio, stats, etc.
   */
  private createUIRedirectSituation(
    type: 'terminal' | 'portfolio' | 'status' | 'help',
    title: string,
    description: string
  ): GeneratedSituation {
    this.isGenerating = false // Reset since we're not actually generating

    const situation: GeneratedSituation = {
      id: generateSituationId(),
      title,
      description,
      category: 'lifestyle',
      location: 'trading-terminal',
      mood: 'dramatic',
      urgency: 'whenever',
      characters: [],
      choices: [],
      visualTags: [`ui:${type}`, 'mood:neutral'],
    }

    this.currentSituation = situation
    this.lastSituationTime = new Date()

    return situation
  }

  /**
   * Generate dynamic contextual actions for the player
   * Returns AI-generated actions based on current state, or defaults on failure
   */
  async generateDynamicActions(session: GameSession): Promise<DynamicAction[]> {
    try {
      const prompt = buildDynamicActionsPrompt(session)

      if (this.useCloudFunctions) {
        // For now, fall back to local generation for dynamic actions
        // Cloud function for this could be added later
        try {
          const response = await generateTextDirect(DYNAMIC_ACTIONS_SYSTEM_PROMPT, prompt)
          const actions = parseDynamicActionsResponse(response)

          if (actions.length > 0) {
            return actions
          }
        } catch (error) {
          console.warn('AI dynamic actions generation failed:', error)
        }
      }

      // Return defaults if AI fails
      return getDefaultDynamicActions(session)
    } catch (error) {
      console.error('Failed to generate dynamic actions:', error)
      return getDefaultDynamicActions(session)
    }
  }

  /**
   * Generate scene image for current situation
   * Returns null if no image could be generated
   */
  async generateSceneImage(situation: GeneratedSituation): Promise<GeneratedImage | null> {
    const style = getStyleForCategory(situation.category)
    const cacheKey = createCacheKey(situation.visualTags, style)

    // 1. Check local cache first (fastest)
    const cached = await imageCache.getImage(cacheKey)
    if (cached) {
      return cached
    }

    // 2. Check for similar images in local cache
    const similar = await imageCache.findSimilarImage(situation.visualTags, 0.8)
    if (similar) {
      return similar
    }

    // 3. Try to generate via Cloud Function (SiliconFlow FLUX)
    if (this.useCloudFunctions) {
      try {
        const generatedImage = await generateImageViaCloud(situation, style)
        if (generatedImage) {
          await imageCache.saveImage(generatedImage, cacheKey)
          return generatedImage
        }
      } catch (cloudError) {
        console.warn('Cloud image generation failed:', cloudError)
      }
    }

    return null
  }

  /**
   * Check if a new situation should be generated
   */
  async shouldGenerateSituation(session: GameSession): Promise<{
    should: boolean
    trigger: SituationGenerationRequest['trigger'] | null
  }> {
    // If already have a current situation, don't generate
    if (this.currentSituation) {
      return { should: false, trigger: null }
    }

    const { shouldGenerate, trigger } = await checkSituationTriggers(
      session,
      this.recentActions,
      this.lastSituationTime
    )

    return { should: shouldGenerate, trigger }
  }

  /**
   * Skip a situation without making a choice
   */
  skipCurrentSituation(): void {
    if (this.currentSituation) {
      // Add skip action
      this.addAction({
        id: `skip-${Date.now()}`,
        type: 'choice',
        description: `Skipped: ${this.currentSituation.title}`,
        timestamp: new Date(),
        situationId: this.currentSituation.id,
      })
    }
    this.currentSituation = null
  }

  /**
   * Find callbacks from consequence log for current context
   */
  findCallbacks(currentCharacters: string[], currentLocation: string): ConsequenceLog[] {
    return findRelevantConsequences(this.consequenceLog, currentCharacters, currentLocation)
  }

  /**
   * Mark a consequence as resolved
   */
  resolveConsequence(consequenceId: string): void {
    const consequence = this.consequenceLog.find(c => c.id === consequenceId)
    if (consequence) {
      consequence.resolved = true
    }
  }

  /**
   * Reset the game master state (for new game)
   */
  reset(): void {
    this.currentSituation = null
    this.consequenceLog = []
    this.recentActions = []
    this.lastSituationTime = null
    this.generatedSituationIds.clear()
    this.isGenerating = false
  }

  /**
   * Load state from persistence
   */
  loadState(state: {
    consequenceLog?: ConsequenceLog[]
    recentActions?: PlayerAction[]
    generatedSituationIds?: string[]
  }): void {
    if (state.consequenceLog) {
      this.consequenceLog = state.consequenceLog
    }
    if (state.recentActions) {
      this.recentActions = state.recentActions
    }
    if (state.generatedSituationIds) {
      this.generatedSituationIds = new Set(state.generatedSituationIds)
    }
  }

  /**
   * Export state for persistence
   */
  exportState(): {
    consequenceLog: ConsequenceLog[]
    recentActions: PlayerAction[]
    generatedSituationIds: string[]
  } {
    return {
      consequenceLog: this.consequenceLog,
      recentActions: this.recentActions,
      generatedSituationIds: Array.from(this.generatedSituationIds),
    }
  }
}

// Export singleton instance
export const gameMaster = GameMaster.getInstance()

/**
 * Loading messages for generation wait times
 */
export const LOADING_MESSAGES = [
  'The universe is conspiring...',
  'Fate is being decided...',
  'Consulting the blockchain oracles...',
  'Summoning degen energy...',
  'Loading alpha...',
  'Checking whale wallets...',
  'Analyzing CT sentiment...',
  'Rolling the cosmic dice...',
  'Manifesting gains...',
  'The trenches are calling...',
]

/**
 * Get a random loading message
 */
export function getLoadingMessage(): string {
  const index = Math.floor(Math.random() * LOADING_MESSAGES.length)
  return LOADING_MESSAGES[index] ?? LOADING_MESSAGES[0] ?? 'Loading...'
}
